import { type MapMouseEvent } from "mapbox-gl";
import { useCallback, useRef, useState } from "react";
import type { MapLayerMouseEvent } from "react-map-gl";
import type {
  MapMode,
  MaterialDetailsProps,
} from "../../../features/collection-map/components/types";
import { useThemedLayer } from "../theme-layer-hook";
import { useMaplibre } from "../utils";
import type { CoordinateMenuProps } from "./coordinate-menu";
import { latLngSchema } from "./lng-lat";
import {
  themedClusterCountLayer,
  themedClusterLayer,
  themedUnclusteredPointLayer,
} from "./material-cluster-layers";
import type { MaterialPopupProps } from "./material-popup";
import { getFeaturePoint, materialGeoJsonPropertiesSchema } from "./utils";
import { isCluster, zoomOnCluster } from "./zoom-on-cluster";

interface useClustersProps {
  mapId: string;
  onPointClick?: (materialDetailsProps: MaterialDetailsProps) => void;
  closeMaterialDetails?: () => void;
  coordinateMenuProps?: CoordinateMenuProps | null;
  openCoordinateMenu?: (props: CoordinateMenuProps) => void;
  closeCoordinateMenu?: () => void;
  mode?: MapMode;
}

export const useClusters = ({
  mapId,
  onPointClick,
  closeMaterialDetails,
  coordinateMenuProps,
  openCoordinateMenu,
  closeCoordinateMenu,
  mode = "view",
}: useClustersProps) => {
  const { [mapId]: ourMap } = useMaplibre();
  const popupHover = useRef(false);
  const hoveredPointId = useRef<string | number | undefined>(undefined);
  const [popupProps, setPopupProps] = useState<Pick<
    MaterialPopupProps,
    | "scientificName"
    | "qualifier"
    | "accessionNumber"
    | "latitude"
    | "longitude"
  > | null>(null);

  const handleClick = useCallback(
    (event: MapLayerMouseEvent) => {
      if (mode !== "view") {
        return;
      }
      const point = getFeaturePoint(event);
      if (point != null) {
        closeCoordinateMenu?.();
        if (isCluster(point)) {
          void zoomOnCluster(ourMap, point);
        }

        if (onPointClick != null) {
          const { success, data } = materialGeoJsonPropertiesSchema.safeParse(
            point.properties,
          );
          if (success) {
            onPointClick({
              accessionNumber: data.accessionNumber,
              materialQualifier: data.qualifier,
            });
          }
        }
      } else if (coordinateMenuProps != null) {
        closeCoordinateMenu?.();
      } else {
        openCoordinateMenu?.({
          latitude: event.lngLat.lat,
          longitude: event.lngLat.lng,
        });
        if (closeMaterialDetails != null) {
          closeMaterialDetails();
        }
      }
    },
    [
      onPointClick,
      ourMap,
      coordinateMenuProps,
      closeCoordinateMenu,
      openCoordinateMenu,
      closeMaterialDetails,
      mode,
    ],
  );

  const onMouseMove = useCallback(
    (event: MapMouseEvent) => {
      // eslint-disable-next-line sonarjs/no-duplicate-string
      if (ourMap?.getLayer("unclustered-point") != null) {
        if (hoveredPointId.current != null) {
          ourMap.setFeatureState(
            { source: "geometry", id: hoveredPointId.current },
            { hover: false },
          );
        }

        const features = ourMap.queryRenderedFeatures(
          [event.point.x, event.point.y],
          {
            layers: ["unclustered-point"],
          },
        );
        const point = getFeaturePoint({ features });

        if (point == null) {
          setPopupProps(null);
          return;
        }

        const { success: propertiesSuccess, data: properties } =
          materialGeoJsonPropertiesSchema.safeParse(point.properties);

        const { success: latLngSuccess, data: latLng } = latLngSchema.safeParse(
          point.geometry.coordinates,
        );

        if (!propertiesSuccess || !latLngSuccess) {
          return;
        }

        const info = {
          latitude: latLng.latitude,
          longitude: latLng.longitude,
          scientificName: properties.scientificName,
          accessionNumber: properties.accessionNumber,
          qualifier: properties.qualifier,
        };

        hoveredPointId.current = point.id;
        // Conditions required to prevent re-render on every mouse move event
        if (JSON.stringify(info) !== JSON.stringify(popupProps)) {
          setPopupProps(info);
        }
        ourMap.setFeatureState(
          { source: "geometry", id: point.id },
          { hover: true },
        );
      }
    },
    [ourMap, popupProps],
  );

  const onMouseLeave = useCallback(() => {
    if (
      ourMap?.getLayer("unclustered-point") != null &&
      hoveredPointId.current != null
    ) {
      ourMap.setFeatureState(
        { source: "geometry", id: hoveredPointId.current },
        { hover: false },
      );
      hoveredPointId.current = undefined;
    }
  }, [ourMap]);

  const layerOpacity = mode === "editMaterial" ? 0.5 : undefined;

  const clusterLayer = useThemedLayer(
    themedClusterLayer,
    "cluster-layer",
    layerOpacity,
  );
  const clusterCountLayer = useThemedLayer(
    themedClusterCountLayer,
    "cluster-count-layer",
    layerOpacity,
  );
  const unclusteredPointLayer = useThemedLayer(
    themedUnclusteredPointLayer,
    "unclustered-point",
    layerOpacity,
  );

  return {
    popupProps,
    handleClick,
    clusterLayer,
    clusterCountLayer,
    unclusteredPointLayer,
    onMouseMove,
    onMouseLeave,
    popupHover,
    hoveredPointId,
  };
};
